import React, { ReactNode, useMemo, useState } from 'react';
import { Squad, User } from '../../../types';
import { groupSquadsByLocationId } from '../utils/locationAnalytics';

type Props = {
  squads: Squad[];
  students: User[];
};

type MonthKey = string;

type MonthStats = {
  key: MonthKey;
  label: string;
  startingSquads: number;
  runningSquads: number;
  studentSeats: number;
  newStudentSeats: number;
  repeatStudentSeats: number;
  averageStudentsPerSquad: number;
  scheduledClasses: number;
};

type RepeatStudentStats = {
  newStudents: number;
  repeatStudents: number;
  totalStudents: number;
  repeatRate: number;
};

type QuarterlyRepeatStats = RepeatStudentStats & {
  quarter: string;
};

type ProgramType = 'Music' | 'AI';

type ProgramConversionStats = {
  firstProgram: ProgramType;
  cohortStudents: number;
  repeatStudents: number;
  repeatRate: number;
  knownSecondProgramStudents: number;
  secondMusicStudents: number;
  secondAiStudents: number;
  unknownSecondRepeatStudents: number;
};

type ProgramPathStats = {
  path: string;
  students: number;
  share: number;
};

type QuarterlyProgramStats = {
  quarter: string;
  musicEnrollments: number;
  aiEnrollments: number;
  musicUniqueStudents: number;
  aiUniqueStudents: number;
  musicNextQuarterRepeats: number;
  aiNextQuarterRepeats: number;
  musicNextQuarterRepeatRate: number;
  aiNextQuarterRepeatRate: number;
};

type GrowthSectionId = 'monthly' | 'retention' | 'programPaths' | 'ogLevels' | 'signals' | 'topMonths' | 'topSchools';

const MONTH_FORMATTER = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

const EXCLUDED_STATUSES = new Set(['canceled', 'cancelled']);
const MIN_STATS_YEAR = 2020;
const MAX_STATS_YEAR = new Date().getUTCFullYear() + 3;
const MAX_MONTHS_PER_SQUAD = 72;
const DAY_INDEX: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

const parseDateKey = (dateKey?: string | null): Date | null => {
  if (!dateKey) return null;
  const match = String(dateKey).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, yearRaw, monthRaw, dayRaw] = match;
  const [year, month, day] = [yearRaw, monthRaw, dayRaw].map(Number);
  if (!year || !month || !day) return null;
  if (year < MIN_STATS_YEAR || year > MAX_STATS_YEAR || month < 1 || month > 12) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
};

const monthKeyFromDate = (date: Date): MonthKey =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;

const monthStart = (key: MonthKey): Date => {
  const [year, month] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1));
};

const monthEnd = (key: MonthKey): Date => {
  const [year, month] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month, 0, 23, 59, 59, 999));
};

const addMonths = (date: Date, count: number): Date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + count, 1));

const formatMonth = (key: MonthKey): string => MONTH_FORMATTER.format(monthStart(key));

const quarterKeyFromDate = (date: Date): string => {
  const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
  return `${date.getUTCFullYear()} Q${quarter}`;
};

const nextQuarterKey = (quarterKey: string): string => {
  const match = quarterKey.match(/^(\d{4}) Q([1-4])$/);
  if (!match) return quarterKey;
  const year = Number(match[1]);
  const quarter = Number(match[2]);
  return quarter === 4 ? `${year + 1} Q1` : `${year} Q${quarter + 1}`;
};

const isExcludedSquad = (squad: Squad): boolean =>
  EXCLUDED_STATUSES.has(String((squad as any).status || '').trim().toLowerCase());

const studentCountForSquad = (squad: Squad): number =>
  Array.isArray(squad.studentIds) ? squad.studentIds.length : 0;

const squadStartDate = (squad: Squad): Date | null =>
  parseDateKey(squad.schedule?.mainDate || squad.schedule?.startDate || squad.firstDate || null);

const squadEndDate = (squad: Squad): Date | null =>
  parseDateKey(squad.schedule?.mainDate || squad.schedule?.endDate || squad.lastDate || squad.schedule?.startDate || squad.firstDate || null);

const monthsBetweenInclusive = (start: Date, end: Date): MonthKey[] => {
  if (end.getTime() < start.getTime()) return [];
  const keys: MonthKey[] = [];
  let cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const final = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));

  while (cursor.getTime() <= final.getTime() && keys.length < MAX_MONTHS_PER_SQUAD) {
    keys.push(monthKeyFromDate(cursor));
    cursor = addMonths(cursor, 1);
  }

  return keys;
};

const monthKeysForSquad = (squad: Squad): MonthKey[] => {
  const start = squadStartDate(squad);
  const end = squadEndDate(squad) || start;
  if (!start || !end) return [];
  return monthsBetweenInclusive(start, end);
};

const countScheduledClassesInMonth = (squad: Squad, key: MonthKey): number => {
  const mainDate = parseDateKey(squad.schedule?.mainDate || null);
  if (mainDate) return monthKeyFromDate(mainDate) === key ? 1 : 0;
  const start = squadStartDate(squad);
  const end = squadEndDate(squad);
  const dayOfWeek = String(squad.schedule?.dayOfWeek || '').trim().toUpperCase();
  const targetDay = DAY_INDEX[dayOfWeek];
  if (!start || !end || targetDay === undefined) return 0;

  const from = new Date(Math.max(start.getTime(), monthStart(key).getTime()));
  const to = new Date(Math.min(end.getTime(), monthEnd(key).getTime()));
  const exceptions = new Set([
    ...(squad.schedule?.exceptions || []),
    ...(squad.schedule?.inheritedExceptions || []),
  ].filter(Boolean));
  const makeups = (squad.schedule?.makeups || []).filter(Boolean);
  let total = 0;

  const cursor = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
  while (cursor.getTime() <= to.getTime()) {
    const dateKey = cursor.toISOString().slice(0, 10);
    if (cursor.getUTCDay() === targetDay && !exceptions.has(dateKey)) {
      total += 1;
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  for (const makeup of makeups) {
    const makeupDate = parseDateKey(makeup);
    if (makeupDate && monthKeyFromDate(makeupDate) === key && !exceptions.has(makeup)) {
      total += 1;
    }
  }

  return total;
};

const percent = (value: number): string =>
  `${Math.round(value * 10) / 10}%`;

const number = (value: number): string =>
  value.toLocaleString(undefined, { maximumFractionDigits: 1 });

const ogLevelForStudent = (student: User | undefined): number => {
  if (!student) return 0;
  const fallbackLevel = Array.isArray((student as any).squadIds) ? (student as any).squadIds.length : 0;
  const rawLevel = Number((student as any).ogLevel ?? fallbackLevel ?? 0);
  return Number.isFinite(rawLevel) ? Math.max(0, Math.floor(rawLevel)) : 0;
};

const programForSquad = (squad: Squad): ProgramType => {
  const curriculum = String(squad.curriculumId || '').toLowerCase();
  return curriculum.includes('ai') ? 'AI' : 'Music';
};

const GrowthAccordionSection: React.FC<{
  id: GrowthSectionId;
  title: string;
  description: string;
  summary: string;
  isOpen: boolean;
  onToggle: (id: GrowthSectionId) => void;
  className?: string;
  children: ReactNode;
}> = ({ id, title, description, summary, isOpen, onToggle, className = '', children }) => (
  <section className={`growth-panel growth-accordion ${isOpen ? 'is-open' : 'is-collapsed'} ${className}`}>
    <button
      type="button"
      className="growth-accordion-trigger"
      onClick={() => onToggle(id)}
      aria-expanded={isOpen}
      aria-controls={`growth-section-${id}`}
    >
      <span className="growth-accordion-copy">
        <span className="growth-accordion-title-row">
          <span className="growth-accordion-chevron">{isOpen ? '−' : '+'}</span>
          <span>
            <strong>{title}</strong>
            <small>{description}</small>
          </span>
        </span>
      </span>
      <span className="growth-accordion-summary">{summary}</span>
    </button>
    {isOpen && (
      <div id={`growth-section-${id}`} className="growth-accordion-body">
        {children}
      </div>
    )}
  </section>
);

const GrowthAnalyticsTab: React.FC<Props> = ({ squads, students }) => {
  const [openSections, setOpenSections] = useState<Record<GrowthSectionId, boolean>>({
    monthly: true,
    retention: true,
    programPaths: true,
    ogLevels: true,
    signals: false,
    topMonths: false,
    topSchools: false,
  });

  const analytics = useMemo(() => {
    const reportableSquads = squads.filter((squad) => !isExcludedSquad(squad));
    const monthStatsByKey = new Map<MonthKey, Omit<MonthStats, 'label' | 'averageStudentsPerSquad'>>();
    const studentById = new Map(students.map((student) => [student.id, student]));
    const firstSquadStartByStudentId = new Map<string, Date>();
    const studentProgramHistory = new Map<string, Array<{ startMs: number; program: ProgramType; squadId: string }>>();
    const quarterlyProgramStatsByKey = new Map<string, QuarterlyProgramStats>();
    const quarterlyProgramStudentsByKey = new Map<string, Record<ProgramType, Set<string>>>();
    const quarterlyAnyProgramStudentsByKey = new Map<string, Set<string>>();

    const ensureQuarterlyProgramStats = (quarter: string): QuarterlyProgramStats => {
      const existing = quarterlyProgramStatsByKey.get(quarter);
      if (existing) return existing;
      const created: QuarterlyProgramStats = {
        quarter,
        musicEnrollments: 0,
        aiEnrollments: 0,
        musicUniqueStudents: 0,
        aiUniqueStudents: 0,
        musicNextQuarterRepeats: 0,
        aiNextQuarterRepeats: 0,
        musicNextQuarterRepeatRate: 0,
        aiNextQuarterRepeatRate: 0,
      };
      quarterlyProgramStatsByKey.set(quarter, created);
      return created;
    };

    const ensureQuarterlyProgramStudents = (quarter: string): Record<ProgramType, Set<string>> => {
      const existing = quarterlyProgramStudentsByKey.get(quarter);
      if (existing) return existing;
      const created = { Music: new Set<string>(), AI: new Set<string>() };
      quarterlyProgramStudentsByKey.set(quarter, created);
      return created;
    };

    for (const squad of reportableSquads) {
      const monthSet = new Set(monthKeysForSquad(squad));
      const start = squadStartDate(squad);
      const startKey = start ? monthKeyFromDate(start) : null;
      const squadStudentCount = studentCountForSquad(squad);
      const program = programForSquad(squad);

      if (start) {
        const quarterKey = quarterKeyFromDate(start);
        const quarterStats = ensureQuarterlyProgramStats(quarterKey);
        const quarterProgramStudents = ensureQuarterlyProgramStudents(quarterKey);
        if (!quarterlyAnyProgramStudentsByKey.has(quarterKey)) {
          quarterlyAnyProgramStudentsByKey.set(quarterKey, new Set());
        }
        const quarterAnyProgramStudents = quarterlyAnyProgramStudentsByKey.get(quarterKey)!;
        if (program === 'AI') quarterStats.aiEnrollments += squadStudentCount;
        else quarterStats.musicEnrollments += squadStudentCount;

        for (const studentId of squad.studentIds || []) {
          quarterProgramStudents[program].add(studentId);
          quarterAnyProgramStudents.add(studentId);

          const previousStart = firstSquadStartByStudentId.get(studentId);
          if (!previousStart || start.getTime() < previousStart.getTime()) {
            firstSquadStartByStudentId.set(studentId, start);
          }

          const history = studentProgramHistory.get(studentId) || [];
          history.push({
            startMs: start.getTime(),
            program,
            squadId: squad.id,
          });
          studentProgramHistory.set(studentId, history);
        }
      }

      for (const key of monthSet) {
        const existing = monthStatsByKey.get(key) || {
          key,
          startingSquads: 0,
          runningSquads: 0,
          studentSeats: 0,
          newStudentSeats: 0,
          repeatStudentSeats: 0,
          scheduledClasses: 0,
        };

        existing.runningSquads += 1;
        existing.studentSeats += squadStudentCount;
        existing.scheduledClasses += countScheduledClassesInMonth(squad, key);

        if (startKey === key) {
          existing.startingSquads += 1;
          for (const studentId of squad.studentIds || []) {
            if (ogLevelForStudent(studentById.get(studentId)) >= 2) {
              existing.repeatStudentSeats += 1;
            } else {
              existing.newStudentSeats += 1;
            }
          }
        }

        monthStatsByKey.set(key, existing);
      }
    }

    const monthStats: MonthStats[] = Array.from(monthStatsByKey.values())
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((month) => ({
        ...month,
        label: formatMonth(month.key),
        averageStudentsPerSquad: month.runningSquads > 0 ? month.studentSeats / month.runningSquads : 0,
      }));

    const ogLevelCounts = new Map<number, number>();
    for (const student of students) {
      const level = ogLevelForStudent(student);
      ogLevelCounts.set(level, (ogLevelCounts.get(level) || 0) + 1);
    }

    const repeatStudentStats: RepeatStudentStats = {
      newStudents: 0,
      repeatStudents: 0,
      totalStudents: 0,
      repeatRate: 0,
    };

    const firstCohortStudentIdsByQuarter = new Map<string, Set<string>>();
    for (const [studentId, firstStart] of firstSquadStartByStudentId.entries()) {
      const quarterKey = quarterKeyFromDate(firstStart);
      if (!firstCohortStudentIdsByQuarter.has(quarterKey)) {
        firstCohortStudentIdsByQuarter.set(quarterKey, new Set());
      }
      firstCohortStudentIdsByQuarter.get(quarterKey)!.add(studentId);
    }

    const quarterlyRepeatStats: QuarterlyRepeatStats[] = Array.from(firstCohortStudentIdsByQuarter.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([quarter, studentIds]) => {
        let quarterRepeatStudents = 0;
        let quarterNewStudents = 0;
        for (const studentId of studentIds) {
          if (ogLevelForStudent(studentById.get(studentId)) >= 2) {
            quarterRepeatStudents += 1;
          } else {
            quarterNewStudents += 1;
          }
        }
        const totalStudents = studentIds.size;
        return {
          quarter,
          newStudents: quarterNewStudents,
          repeatStudents: quarterRepeatStudents,
          totalStudents,
          repeatRate: totalStudents > 0 ? (quarterRepeatStudents / totalStudents) * 100 : 0,
        };
      });

    for (const row of quarterlyRepeatStats) {
      repeatStudentStats.newStudents += row.newStudents;
      repeatStudentStats.repeatStudents += row.repeatStudents;
      repeatStudentStats.totalStudents += row.totalStudents;
    }
    repeatStudentStats.repeatRate = repeatStudentStats.totalStudents > 0
      ? (repeatStudentStats.repeatStudents / repeatStudentStats.totalStudents) * 100
      : 0;

    const ogLevels = Array.from(ogLevelCounts.entries())
      .sort(([a], [b]) => a - b)
      .map(([level, count]) => ({ level, count }));

    const programMix = reportableSquads.reduce(
      (acc, squad) => {
        const curriculum = String(squad.curriculumId || '').toLowerCase();
        if (curriculum.includes('ai')) acc.ai += 1;
        else acc.music += 1;
        return acc;
      },
      { ai: 0, music: 0 }
    );

    const programConversionByFirstProgram = new Map<ProgramType, ProgramConversionStats>([
      ['Music', {
        firstProgram: 'Music',
        cohortStudents: 0,
        repeatStudents: 0,
        repeatRate: 0,
        knownSecondProgramStudents: 0,
        secondMusicStudents: 0,
        secondAiStudents: 0,
        unknownSecondRepeatStudents: 0,
      }],
      ['AI', {
        firstProgram: 'AI',
        cohortStudents: 0,
        repeatStudents: 0,
        repeatRate: 0,
        knownSecondProgramStudents: 0,
        secondMusicStudents: 0,
        secondAiStudents: 0,
        unknownSecondRepeatStudents: 0,
      }],
    ]);
    const firstToSecondOrderCounts = new Map<string, number>();
    const fullPathCounts = new Map<string, number>();
    let knownSecondProgramTotal = 0;
    let repeatHistoryTotal = 0;

    for (const [studentId, rawHistory] of studentProgramHistory.entries()) {
      const history = [...rawHistory].sort((a, b) => a.startMs - b.startMs || a.squadId.localeCompare(b.squadId));
      const first = history[0];
      if (!first) continue;
      const conversion = programConversionByFirstProgram.get(first.program)!;
      const isRepeat = ogLevelForStudent(studentById.get(studentId)) >= 2;
      const second = history[1] || null;

      conversion.cohortStudents += 1;
      if (isRepeat) {
        conversion.repeatStudents += 1;
        if (!second) conversion.unknownSecondRepeatStudents += 1;
      }

      if (second) {
        conversion.knownSecondProgramStudents += 1;
        knownSecondProgramTotal += 1;
        if (second.program === 'AI') conversion.secondAiStudents += 1;
        else conversion.secondMusicStudents += 1;

        const order = `${first.program} -> ${second.program}`;
        firstToSecondOrderCounts.set(order, (firstToSecondOrderCounts.get(order) || 0) + 1);

        const fullPath = history.map(item => item.program).join(' -> ');
        fullPathCounts.set(fullPath, (fullPathCounts.get(fullPath) || 0) + 1);
        repeatHistoryTotal += 1;
      }
    }

    const programConversionStats = Array.from(programConversionByFirstProgram.values()).map(row => ({
      ...row,
      repeatRate: row.cohortStudents > 0 ? (row.repeatStudents / row.cohortStudents) * 100 : 0,
    }));

    const quarterlyProgramStats: QuarterlyProgramStats[] = Array.from(quarterlyProgramStatsByKey.values())
      .sort((a, b) => a.quarter.localeCompare(b.quarter))
      .map(row => {
        const programStudents = quarterlyProgramStudentsByKey.get(row.quarter);
        const nextQuarterStudents = quarterlyAnyProgramStudentsByKey.get(nextQuarterKey(row.quarter)) || new Set<string>();
        const musicStudents = programStudents?.Music || new Set<string>();
        const aiStudents = programStudents?.AI || new Set<string>();
        const musicNextQuarterRepeats = Array.from(musicStudents).filter(studentId => nextQuarterStudents.has(studentId)).length;
        const aiNextQuarterRepeats = Array.from(aiStudents).filter(studentId => nextQuarterStudents.has(studentId)).length;

        return {
          ...row,
          musicUniqueStudents: musicStudents.size,
          aiUniqueStudents: aiStudents.size,
          musicNextQuarterRepeats,
          aiNextQuarterRepeats,
          musicNextQuarterRepeatRate: musicStudents.size > 0 ? (musicNextQuarterRepeats / musicStudents.size) * 100 : 0,
          aiNextQuarterRepeatRate: aiStudents.size > 0 ? (aiNextQuarterRepeats / aiStudents.size) * 100 : 0,
        };
      });

    const firstToSecondOrders: ProgramPathStats[] = Array.from(firstToSecondOrderCounts.entries())
      .map(([path, students]) => ({
        path,
        students,
        share: knownSecondProgramTotal > 0 ? (students / knownSecondProgramTotal) * 100 : 0,
      }))
      .sort((a, b) => b.students - a.students || a.path.localeCompare(b.path));

    const fullProgramPaths: ProgramPathStats[] = Array.from(fullPathCounts.entries())
      .map(([path, students]) => ({
        path,
        students,
        share: repeatHistoryTotal > 0 ? (students / repeatHistoryTotal) * 100 : 0,
      }))
      .sort((a, b) => b.students - a.students || a.path.localeCompare(b.path))
      .slice(0, 10);

    const missingTeacherCount = reportableSquads.filter((squad) => {
      const hasRegularTeacher = !!String((squad as any).regularTeacher || squad.leadTeacherId || '').trim();
      const hasTeacherIds = Array.isArray(squad.teacherIds) && squad.teacherIds.length > 0;
      return !hasRegularTeacher && !hasTeacherIds;
    }).length;

    const currentMonth = monthKeyFromDate(new Date());
    const currentMonthStats = monthStats.find((m) => m.key === currentMonth) || monthStats[monthStats.length - 1] || null;

    const topEnrollmentMonths = [...monthStats]
      .sort((a, b) => b.studentSeats - a.studentSeats)
      .slice(0, 6);

    const topLocations = groupSquadsByLocationId(reportableSquads).slice(0, 8);

    return {
      reportableSquads,
      monthStats,
      repeatStudentStats,
      quarterlyRepeatStats,
      ogLevels,
      programConversionStats,
      quarterlyProgramStats,
      firstToSecondOrders,
      fullProgramPaths,
      programMix,
      missingTeacherCount,
      currentMonthStats,
      topEnrollmentMonths,
      topLocations,
    };
  }, [squads, students]);

  const toggleSection = (id: GrowthSectionId) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="growth-analytics-tab">
      <div className="growth-hero">
        <div>
          <p className="growth-eyebrow">Admin Statistics</p>
          <h2>Growth Analytics</h2>
          <p>
            Scheduled squad dates and assigned student rosters power these metrics. Archived squads count as historical
            classes; canceled squads are excluded.
          </p>
        </div>
        <div className="growth-hero-note">
          <strong>Repeat conversion logic</strong>
          <span>Quarterly repeat rate is based on each student’s first known squad start quarter. OG 2+ means they eventually repeated.</span>
        </div>
      </div>

      <div className="growth-kpi-grid">
        <div className="growth-kpi-card">
          <span className="growth-kpi-label">Squads Running This Month</span>
          <strong>{analytics.currentMonthStats?.runningSquads ?? 0}</strong>
          <small>{analytics.currentMonthStats?.scheduledClasses ?? 0} scheduled classes</small>
        </div>
        <div className="growth-kpi-card">
          <span className="growth-kpi-label">Avg Students / Squad</span>
          <strong>{number(analytics.currentMonthStats?.averageStudentsPerSquad ?? 0)}</strong>
          <small>{analytics.currentMonthStats?.studentSeats ?? 0} student seats this month</small>
        </div>
        <div className="growth-kpi-card">
          <span className="growth-kpi-label">Overall Repeat Conversion</span>
          <strong>{percent(analytics.repeatStudentStats.repeatRate)}</strong>
          <small>{analytics.repeatStudentStats.repeatStudents}/{analytics.repeatStudentStats.totalStudents} first-cohort students became OG 2+</small>
        </div>
        <div className="growth-kpi-card">
          <span className="growth-kpi-label">Reportable Squads</span>
          <strong>{analytics.reportableSquads.length}</strong>
          <small>Includes archived historical squads</small>
        </div>
      </div>

      <GrowthAccordionSection
        id="monthly"
        title="Monthly Squad Trends"
        description="Monthly enrollments are seats, so one student can count more than once if they are assigned to multiple squads."
        summary={`${analytics.monthStats.length} months`}
        isOpen={openSections.monthly}
        onToggle={toggleSection}
        className="growth-panel-full"
      >
        <div className="growth-table-scroll">
          <table className="growth-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Squads Starting</th>
                <th>Squads Running</th>
                <th>Avg Students / Squad</th>
                <th>Student Seats</th>
                <th>New Starting Seats</th>
                <th>Repeat Starting Seats</th>
                <th>Scheduled Classes</th>
              </tr>
            </thead>
            <tbody>
              {analytics.monthStats.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: '#6b7280' }}>No scheduled squads found.</td>
                </tr>
              ) : analytics.monthStats.map((month) => (
                <tr key={month.key}>
                  <td style={{ fontWeight: 700 }}>{month.label}</td>
                  <td style={{ textAlign: 'right' }}>{month.startingSquads}</td>
                  <td style={{ textAlign: 'right' }}>{month.runningSquads}</td>
                  <td style={{ textAlign: 'right' }}>{number(month.averageStudentsPerSquad)}</td>
                  <td style={{ textAlign: 'right' }}>{month.studentSeats.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{month.newStudentSeats.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{month.repeatStudentSeats.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{month.scheduledClasses.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GrowthAccordionSection>

      <GrowthAccordionSection
        id="programPaths"
        title="Program Repeat Paths"
        description="By quarter: Music/AI enrollments, unique students, and how many enrolled again in the next quarter."
        summary={`${analytics.firstToSecondOrders.length} orders`}
        isOpen={openSections.programPaths}
        onToggle={toggleSection}
        className="growth-panel-full"
      >
        <div className="growth-table-scroll" style={{ marginBottom: '1rem' }}>
          <table className="growth-table">
            <thead>
              <tr>
                <th>Quarter</th>
                <th>Music Enrollments</th>
                <th>AI Enrollments</th>
                <th>Music Unique Students</th>
                <th>AI Unique Students</th>
                <th>Music Next-Q Repeat</th>
                <th>AI Next-Q Repeat</th>
              </tr>
            </thead>
            <tbody>
              {analytics.quarterlyProgramStats.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: '#6b7280' }}>No program enrollments with start dates found.</td>
                </tr>
              ) : analytics.quarterlyProgramStats.map((row) => (
                <tr key={row.quarter}>
                  <td style={{ fontWeight: 700 }}>{row.quarter}</td>
                  <td style={{ textAlign: 'right' }}>{row.musicEnrollments.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{row.aiEnrollments.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{row.musicUniqueStudents.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{row.aiUniqueStudents.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {row.musicNextQuarterRepeats.toLocaleString()} / {row.musicUniqueStudents.toLocaleString()} ({percent(row.musicNextQuarterRepeatRate)})
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {row.aiNextQuarterRepeats.toLocaleString()} / {row.aiUniqueStudents.toLocaleString()} ({percent(row.aiNextQuarterRepeatRate)})
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="growth-two-column">
          <div className="growth-table-scroll">
            <table className="growth-table">
              <thead>
                <tr>
                  <th>First Program</th>
                  <th>Cohort Students</th>
                  <th>Repeat (OG 2+)</th>
                  <th>Repeat Conversion</th>
                  <th>2nd Music</th>
                  <th>2nd AI</th>
                  <th>Repeat, 2nd Unknown</th>
                </tr>
              </thead>
              <tbody>
                {analytics.programConversionStats.map((row) => (
                  <tr key={row.firstProgram}>
                    <td style={{ fontWeight: 700 }}>{row.firstProgram}</td>
                    <td style={{ textAlign: 'right' }}>{row.cohortStudents.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{row.repeatStudents.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{percent(row.repeatRate)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {row.secondMusicStudents.toLocaleString()}
                      {row.knownSecondProgramStudents > 0 ? ` (${percent((row.secondMusicStudents / row.knownSecondProgramStudents) * 100)})` : ''}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {row.secondAiStudents.toLocaleString()}
                      {row.knownSecondProgramStudents > 0 ? ` (${percent((row.secondAiStudents / row.knownSecondProgramStudents) * 100)})` : ''}
                    </td>
                    <td style={{ textAlign: 'right' }}>{row.unknownSecondRepeatStudents.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="growth-table-scroll">
            <table className="growth-table">
              <thead>
                <tr>
                  <th>{'First -> Second Order'}</th>
                  <th>Students</th>
                  <th>Share of Known 2nd Classes</th>
                </tr>
              </thead>
              <tbody>
                {analytics.firstToSecondOrders.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center', color: '#6b7280' }}>No students with a known second class found.</td>
                  </tr>
                ) : analytics.firstToSecondOrders.map((row) => (
                  <tr key={row.path}>
                    <td style={{ fontWeight: 700 }}>{row.path}</td>
                    <td style={{ textAlign: 'right' }}>{row.students.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{percent(row.share)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="growth-table-scroll" style={{ marginTop: '1rem' }}>
          <table className="growth-table">
            <thead>
              <tr>
                <th>Most Common Known Program Paths</th>
                <th>Students</th>
                <th>Share of Repeat Histories</th>
              </tr>
            </thead>
            <tbody>
              {analytics.fullProgramPaths.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', color: '#6b7280' }}>No repeat histories found.</td>
                </tr>
              ) : analytics.fullProgramPaths.map((row) => (
                <tr key={row.path}>
                  <td style={{ fontWeight: 700 }}>{row.path}</td>
                  <td style={{ textAlign: 'right' }}>{row.students.toLocaleString()}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{percent(row.share)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GrowthAccordionSection>

      <div className="growth-two-column">
        <GrowthAccordionSection
          id="retention"
          title="Quarterly Repeat Conversion"
          description="For each first-start quarter, what percent of students eventually became OG 2+."
          summary={`${analytics.quarterlyRepeatStats.length} quarters`}
          isOpen={openSections.retention}
          onToggle={toggleSection}
        >
          <div className="growth-table-scroll">
            <table className="growth-table">
              <thead>
                <tr>
                  <th>First Squad Quarter</th>
                  <th>Cohort Students</th>
                  <th>New (OG 0-1)</th>
                  <th>Repeat (OG 2+)</th>
                  <th>Repeat Conversion</th>
                </tr>
              </thead>
              <tbody>
                {analytics.quarterlyRepeatStats.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#6b7280' }}>No student cohorts with first squad start dates found.</td>
                  </tr>
                ) : analytics.quarterlyRepeatStats.map((row) => (
                  <tr key={row.quarter}>
                    <td style={{ fontWeight: 700 }}>{row.quarter}</td>
                    <td style={{ textAlign: 'right' }}>{row.totalStudents.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{row.newStudents.toLocaleString()}</td>
                    <td style={{ textAlign: 'right' }}>{row.repeatStudents.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{percent(row.repeatRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GrowthAccordionSection>

        <GrowthAccordionSection
          id="ogLevels"
          title="Students by OG Level"
          description="Current student level distribution."
          summary={`${students.length.toLocaleString()} students`}
          isOpen={openSections.ogLevels}
          onToggle={toggleSection}
        >
          <div className="growth-table-scroll">
            <table className="growth-table">
              <thead>
                <tr>
                  <th>OG Level</th>
                  <th>Students</th>
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                {analytics.ogLevels.map((row) => {
                  const share = students.length > 0 ? (row.count / students.length) * 100 : 0;
                  return (
                    <tr key={row.level}>
                      <td style={{ fontWeight: 700 }}>OG {row.level}</td>
                      <td style={{ textAlign: 'right' }}>{row.count}</td>
                      <td style={{ textAlign: 'right' }}>{percent(share)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </GrowthAccordionSection>
      </div>

      <div className="growth-three-column">
        <GrowthAccordionSection
          id="signals"
          title="Operating Signals"
          description="Quick checks for scale and staffing."
          summary={`${analytics.missingTeacherCount} missing teachers`}
          isOpen={openSections.signals}
          onToggle={toggleSection}
        >
          <div className="growth-table-scroll">
            <table className="growth-table">
              <tbody>
                <tr>
                  <td>Scheduled squads incl. archived</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{analytics.reportableSquads.length}</td>
                </tr>
                <tr>
                  <td>Music squads</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{analytics.programMix.music}</td>
                </tr>
                <tr>
                  <td>AI squads</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{analytics.programMix.ai}</td>
                </tr>
                <tr>
                  <td>Squads missing teacher assignment</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>{analytics.missingTeacherCount}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </GrowthAccordionSection>

        <GrowthAccordionSection
          id="topMonths"
          title="Highest Enrollment Months"
          description="Months with the most student seats."
          summary={`${analytics.topEnrollmentMonths.length} rows`}
          isOpen={openSections.topMonths}
          onToggle={toggleSection}
        >
          <div className="growth-table-scroll">
            <table className="growth-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Seats</th>
                  <th>Running Squads</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topEnrollmentMonths.map((month) => (
                  <tr key={month.key}>
                    <td>{month.label}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{month.studentSeats}</td>
                    <td style={{ textAlign: 'right' }}>{month.runningSquads}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GrowthAccordionSection>

        <GrowthAccordionSection
          id="topSchools"
          title="Top Locations by Seats"
          description="Current assigned seats across reportable squads."
          summary={`${analytics.topLocations.length} locations`}
          isOpen={openSections.topSchools}
          onToggle={toggleSection}
        >
          <div className="growth-table-scroll">
            <table className="growth-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Squads</th>
                  <th>Current Seats</th>
                  <th>Avg Seats / Squad</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topLocations.map((row) => (
                  <tr key={row.locationId}>
                    <td style={{ fontWeight: 700 }}>{row.label}</td>
                    <td style={{ textAlign: 'right' }}>{row.squads}</td>
                    <td style={{ textAlign: 'right' }}>{row.seats}</td>
                    <td style={{ textAlign: 'right' }}>{number(row.squads > 0 ? row.seats / row.squads : 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GrowthAccordionSection>
      </div>
    </div>
  );
};

export default GrowthAnalyticsTab;
